import React from 'react';
import { Box, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import { SeasonBadge } from './Badges';
import { displayTitle, thumbTarget } from './format';
import { C, MONO, RADIUS } from './tokens';
import { RestockV2DeferredAw, RestockV2Order, RestockV2StaleOrder, RestockV2ThumbTarget } from './types';

// 2026-10-04 (Magnus-approved readout rules). Two lists the Korea order no longer carries, and that
// must still be on the 訂貨 screen — a line may leave the ORDER, never the SCREEN:
//   秋款先不加      rule 7: AW additions held back while the projected rail is above the AW target + 10.
//   下單超過 7 天未到 rule 5: open Korea lines older than 7 days. They no longer count as ordered, so
//                    the engine may order the piece again — chase the supplier before pasting.
// Neither is tickable and neither is in the copied order.

const chip = (color: string | null, size: string | null, qty: number) =>
  `${[color, size].filter(Boolean).join(' ') || '單一款'} ×${qty}`;

const Row: React.FC<{
  sku: string; title: string; image_url?: string | null; season?: RestockV2DeferredAw['season'];
  cat?: string | null; lines: string; reason: string; qty: number; onImage: (target: RestockV2ThumbTarget) => void;
  dataKey: string;
}> = ({ sku, title, image_url, season, cat, lines, reason, qty, onImage, dataKey }) => (
  <Box
    component="article" data-held={dataKey}
    sx={{ bgcolor: C.card, border: '1px dashed', borderColor: C.line2, borderRadius: `${RADIUS}px`, p: 1.1, mb: 0.9 }}
  >
    <Box sx={{ display: 'flex', gap: 1.25 }}>
      <ProductThumb
        target={thumbTarget({ sku, title: title || sku, image_url: image_url ?? null })}
        width={52} height={69} radius={7} onOpen={onImage}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
          <SeasonBadge value={season ?? null} />
          {!!cat && <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>{cat}</Typography>}
          <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{sku}</Typography>
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1.3 }}>{displayTitle(title || sku)}</Typography>
        <Typography sx={{ fontSize: 12, color: C.ink2, mt: '2px' }}>{lines}</Typography>
        <Typography sx={{ fontSize: 12, color: C.wait, fontWeight: 700, mt: '2px' }}>{reason}</Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flex: 'none' }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>{qty}</Typography>
        <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>件</Typography>
      </Box>
    </Box>
  </Box>
);

const Heading: React.FC<{ title: string; note: string; count: number; pieces: number }> = ({ title, note, count, pieces }) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '2px', pb: 0.5 }}>
      <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 900 }}>
        {title}
        <Box component="span" sx={{ color: C.ink3, fontWeight: 700, fontSize: 12, ml: '5px' }}>{count} 款</Box>
      </Typography>
      <Typography sx={{ ml: 'auto', fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.wait, bgcolor: C.paper2, borderRadius: '99px', px: 1 }}>
        {pieces} 件
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 12, color: C.ink2, px: '2px', pb: 0.9 }}>{note}</Typography>
  </>
);

export const DEFERRED_NOTE = '秋款在架上的比例已經比目標高超過 10 個百分點，這些新的秋款這次先不訂；賣完補回的件數不受影響。';
export const STALE_NOTE = '這些韓國訂單下單超過 7 天還沒到，引擎不再把它們當成「已訂」，可能會再訂一次；請先跟店家確認。';

const HeldBackSection: React.FC<{
  order: RestockV2Order | undefined; onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ order, onImage }) => {
  const deferred: RestockV2DeferredAw[] = order?.deferred_aw || [];
  const stale: RestockV2StaleOrder[] = order?.stale_orders || [];
  if (!deferred.length && !stale.length) return null;
  const sum = (rows: Array<{ qty?: number; deferred_qty?: number }>, key: 'qty' | 'deferred_qty') =>
    rows.reduce((n, row) => n + (Number(row[key]) || 0), 0);
  return (
    <Box component="section" data-held-back-section="" sx={{ mt: 2.5 }}>
      {deferred.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Heading title="秋款先不加" note={DEFERRED_NOTE} count={deferred.length} pieces={sum(deferred, 'deferred_qty')} />
          {deferred.map((row) => (
            <Row
              key={`d-${row.sku}`} dataKey={`deferred-${row.sku}`} sku={row.sku} title={row.title} image_url={row.image_url}
              season={row.season} cat={row.cat} qty={row.deferred_qty} reason={row.reason} onImage={onImage}
              lines={row.variants.map((v) => chip(v.color, v.size, v.deferred_qty)).join('、')}
            />
          ))}
        </Box>
      )}
      {stale.length > 0 && (
        <Box>
          <Heading title="下單超過 7 天未到" note={STALE_NOTE} count={new Set(stale.map((r) => r.sku)).size} pieces={sum(stale, 'qty')} />
          {stale.map((row) => (
            <Row
              key={`s-${row.sku}-${row.variant_id}`} dataKey={`stale-${row.sku}-${row.variant_id}`} sku={row.sku}
              title={row.title} image_url={row.image_url} season={row.season} cat={row.cat} qty={row.qty} onImage={onImage}
              reason={row.age_days != null ? `${row.reason}（${row.age_days} 天）` : row.reason}
              lines={chip(row.color, row.size, row.qty)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default HeldBackSection;
